import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Lock, CheckCircle2, Loader2, Sparkles, Send } from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { useApp } from '@/context/AppContext';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const ALL_SUITES = [
  'talentmap', 'talentcheck', 'talentstart', 'talentheart', 'talentfix',
  'climara', 'talentcompetency', 'talenttrain',
  'workforce_records', 'talentexit', 'talentperformance',
  // CTH-044 — 'intelligence' (TalentIntelligence) is hidden from the
  // commercial catalog until the Founder authorizes its launch. Code,
  // routes and data remain intact.
];

// Founder UAT (I) — every active suite has a real Client Portal workspace.
// Routes verified against ClientRoutes.jsx; never fabricated.
const SUITE_CONSOLE_PATHS = {
  talentmap: '/client/talentmap',
  talentcheck: '/client/talentcheck',
  talentstart: '/client/talentstart',
  talentheart: '/client/talentheart',
  talentfix: '/client/talentfix',
  climara: '/client/climara',
  talentcompetency: '/client/talentcompetency',
  talenttrain: '/client/talenttrain',
  intelligence: '/client/talentintelligence',
  workforce_records: '/client/workforce-records',
  talentexit: '/client/talentexit',
  talentperformance: '/client/talentperformance',
};

function RequestDialog({ open, onOpenChange, suiteCode, onCreated }) {
  const { t } = useTranslation();
  const [justification, setJustification] = useState('');
  const [urgency, setUrgency] = useState('medium');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setJustification('');
      setUrgency('medium');
    }
  }, [open]);

  const submit = async () => {
    if (justification.trim().length < 4) {
      toast.error(t('suites.clientCatalog.justificationPh'));
      return;
    }
    setSubmitting(true);
    try {
      await clientApi.createSuiteRequest({
        suite_code: suiteCode,
        justification: justification.trim(),
        urgency,
      });
      toast.success(t('suites.clientCatalog.successToast'), {
        description: t('suites.clientCatalog.successDesc'),
      });
      onCreated && onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(t('suites.clientCatalog.failToast'), { description: formatApiError(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const suiteName = t(`suites.clientCatalog.suiteDefs.${suiteCode}.name`, { defaultValue: suiteCode });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="suite-request-dialog">
        <DialogHeader>
          <DialogTitle>{t('suites.clientCatalog.requestDialogTitle')}</DialogTitle>
          <DialogDescription>{t('suites.clientCatalog.requestDialogDesc')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-1">
          <div className="space-y-1.5">
            <Label>{t('suites.clientCatalog.suiteField')}</Label>
            <div
              data-testid="suite-request-name"
              className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm font-medium"
            >
              {suiteName}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t('suites.clientCatalog.justificationField')}</Label>
            <Textarea
              data-testid="suite-request-justification"
              rows={4}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder={t('suites.clientCatalog.justificationPh')}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('suites.clientCatalog.urgencyField')}</Label>
            <Select value={urgency} onValueChange={setUrgency}>
              <SelectTrigger data-testid="suite-request-urgency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">{t('suites.clientCatalog.urgencyLow')}</SelectItem>
                <SelectItem value="medium">{t('suites.clientCatalog.urgencyMedium')}</SelectItem>
                <SelectItem value="high">{t('suites.clientCatalog.urgencyHigh')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="suite-request-cancel">
            {t('suites.clientCatalog.cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-gradient-primary hover:opacity-90 gap-1.5"
            data-testid="suite-request-submit"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> {t('suites.clientCatalog.submitting')}</>
            ) : (
              <><Send className="h-4 w-4" /> {t('suites.clientCatalog.submit')}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ClientSuites() {
  const { t } = useTranslation();
  const { user } = useApp();
  const isClientAdmin = user?.role === 'client_admin';
  const [company, setCompany] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeSuite, setActiveSuite] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [co, reqs] = await Promise.all([
        clientApi.getMyCompany(),
        clientApi.listSuiteRequests().catch(() => []),
      ]);
      setCompany(co || null);
      setPendingRequests((reqs || []).filter((r) => r.status === 'pending'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeSet = useMemo(
    () => new Set(company?.suite_codes || []),
    [company],
  );
  const pendingSet = useMemo(
    () => new Set(pendingRequests.map((r) => r.suite_code)),
    [pendingRequests],
  );

  const openRequestFor = (code) => {
    setActiveSuite(code);
    setDialogOpen(true);
  };

  return (
    <>
      <Topbar
        title={t('suites.clientCatalog.title')}
        subtitle={t('suites.clientCatalog.subtitle')}
      />
      <div className="p-6 lg:p-8">
        {loading ? (
          <div className="py-16 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="client-suites-grid">
            {ALL_SUITES.map((code) => {
              const active = activeSet.has(code);
              const pending = pendingSet.has(code);
              const name = t(`suites.clientCatalog.suiteDefs.${code}.name`, { defaultValue: code });
              const desc = t(`suites.clientCatalog.suiteDefs.${code}.desc`, { defaultValue: '' });
              const benefits = [1, 2, 3].map((i) =>
                t(`suites.clientCatalog.suiteDefs.${code}.b${i}`, { defaultValue: '' }),
              ).filter(Boolean);

              return (
                <Card
                  key={code}
                  data-testid={`suite-card-${code}`}
                  className={`relative p-5 shadow-card transition-smooth ${
                    active ? '' : 'bg-muted/30'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {active ? (
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-success/15 text-success">
                          <Sparkles className="h-4 w-4" />
                        </span>
                      ) : (
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-muted-foreground/15 text-muted-foreground">
                          <Lock className="h-4 w-4" />
                        </span>
                      )}
                      <h3 className="font-semibold text-base">{name}</h3>
                    </div>
                    {active ? (
                      <Badge
                        className="bg-success/15 text-success border-success/30 gap-1"
                        data-testid={`suite-badge-active-${code}`}
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        {t('suites.clientCatalog.activeBadge')}
                      </Badge>
                    ) : pending ? (
                      <Badge
                        className="bg-warning/10 text-warning border-warning/30 gap-1"
                        variant="outline"
                        data-testid={`suite-badge-pending-${code}`}
                      >
                        {t('suites.clientCatalog.pendingTag')}
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="gap-1"
                        data-testid={`suite-badge-locked-${code}`}
                      >
                        {t('phase3.suitesClient.availableBadge', { defaultValue: 'Available' })}
                      </Badge>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    {desc}
                  </p>

                  {benefits.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                        {t('suites.clientCatalog.benefitsTitle')}
                      </p>
                      <ul className="space-y-1.5">
                        {benefits.map((b, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 text-xs text-foreground/80"
                            data-testid={`suite-benefit-${code}-${i}`}
                          >
                            <CheckCircle2 className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${active ? 'text-success' : 'text-muted-foreground'}`} />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Founder UAT (I) — status lives in the badge above; this row is ACTION only. */}
                  <div className="pt-3 border-t border-border/60 flex items-center justify-between">
                    {active ? (
                      <Link to={SUITE_CONSOLE_PATHS[code]} className="flex-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full"
                          data-testid={`suite-open-${code}`}
                        >
                          {t('suites.clientCatalog.openCta')}
                        </Button>
                      </Link>
                    ) : pending ? (
                      <p className="text-xs text-muted-foreground" data-testid={`suite-pending-${code}`}>
                        {t('phase3.suitesClient.pendingInfo', { defaultValue: 'Under review by Corporate Talent Hub' })}
                      </p>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => openRequestFor(code)}
                        className="bg-gradient-primary hover:opacity-90 gap-1.5 w-full"
                        data-testid={`suite-request-${code}`}
                      >
                        <Send className="h-3.5 w-3.5" /> {t('suites.clientCatalog.requestCta')}
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}

            {/* Founder UAT (H/R) — Global Ethics: separately provisioned service,
                truthfully represented instead of silently omitted. */}
            <Card className="relative p-5 shadow-card transition-smooth" data-testid="suite-card-globalethics">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Sparkles className="h-4 w-4" />
                  </span>
                  <h3 className="font-semibold text-base">Global Ethics</h3>
                </div>
                <Badge variant="outline" className="gap-1 text-xs border-primary/40 text-primary bg-primary/5" data-testid="suite-badge-ge">
                  {isClientAdmin
                    ? t('phase3.suitesClient.geBadge', { defaultValue: 'Activa' })
                    : t('phase3.suitesClient.gePendingBadge', { defaultValue: 'Pendiente de configuración' })}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                {t('phase3.suitesClient.geDesc', { defaultValue: 'Confidential ethics line and case management. Activation is coordinated directly with Corporate Talent Hub.' })}
              </p>
              <div className="pt-3 border-t border-border/60">
                {isClientAdmin ? (
                  <Link to="/client/global-ethics" className="block">
                    <Button size="sm" variant="outline" className="w-full" data-testid="suite-open-globalethics">
                      {t('suites.clientCatalog.openCta')}
                    </Button>
                  </Link>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="suite-ge-managed">
                    {t('phase3.suitesClient.geManaged', { defaultValue: 'Coordinated directly with Corporate Talent Hub.' })}
                  </p>
                )}
              </div>
            </Card>
          </div>
        )}

        {activeSuite && (
          <RequestDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            suiteCode={activeSuite}
            onCreated={load}
          />
        )}
      </div>
    </>
  );
}
